import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface BottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
    className?: string;
    showHeader?: boolean;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
    isOpen,
    onClose,
    children,
    title,
    className = '',
    showHeader = true
}) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        const handleClickOutside = (e: MouseEvent) => {
            if (overlayRef.current === e.target) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>
                {`
                    @keyframes slideUp {
                        from {
                            transform: translateY(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateY(0);
                            opacity: 1;
                        }
                    }
                `}
            </style>
            <div
                ref={overlayRef}
                className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center transition-opacity duration-300 ease-in-out"
                onClick={onClose}
            >
                <div
                    ref={contentRef}
                    className={`
                        w-full max-w-2xl shadow-xl
                        transform transition-all duration-300 ease-in-out
                        flex flex-col translate-y-0 opacity-100
                        !rounded-t-2xl sm:!rounded-2xl sm:mx-4
                        overflow-hidden
                        ${className}
                    `}
                    style={{
                        animation: 'slideUp 0.3s ease-out',
                        backgroundColor: 'var(--bg-surface)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    {showHeader && (
                        <div className="flex-shrink-0 flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border)' }}>
                            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-base)' }}>{title}</h2>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-full transition-colors"
                                style={{ color: 'var(--text-muted)' }}
                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--bg-app)')}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                    )}

                    {/* Content */}
                    <div className="flex flex-col">
                        {children}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default BottomSheet; 